/*
*   Copyright 2026 Dynatrace LLC
*
*   Licensed under the Apache License, Version 2.0 (the "License");
*   you may not use this file except in compliance with the License.
*   You may obtain a copy of the License at
*
*   http://www.apache.org/licenses/LICENSE-2.0
*
*   Unless required by applicable law or agreed to in writing, software
*   distributed under the License is distributed on an "AS IS" BASIS,
*   WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
*   See the License for the specific language governing permissions and
*   limitations under the License.
*/

// DO NOT EDIT, this is an Auto-generated file from ./templates/typescript_constants.ts.j2

/**
* Artifact Version
*/
export const ARTIFACT_VERSION = "0.36.4.20260115.164246";

// -----------------------------------------------------------------------------

/**
* Prefix for 'cloud'
*/
export const CLOUD_PREFIX = "cloud";

/**
* Prefix for 'container'
*/
export const CONTAINER_PREFIX = "container";

/**
* Prefix for 'dt.cloud.gcp'
*/
export const DT_CLOUD_GCP_PREFIX = "gcp";

/**
* Prefix for 'dt.ctg_resource'
*/
export const DT_CTG_RESOURCE_PREFIX = "dt.ctg";

/**
* Prefix for 'dt.env_vars'
*/
export const DT_ENV_VARS_PREFIX = "dt.env_vars";

/**
* Prefix for 'dt.faas_resource'
*/
export const DT_FAAS_RESOURCE_PREFIX = "dt.faas";

/**
* Prefix for 'dt.ims_resource'
*/
export const DT_IMS_RESOURCE_PREFIX = "dt.ims";

/**
* Prefix for 'dt.os'
*/
export const DT_OS_PREFIX = "dt.os";

/**
* Prefix for 'dt.osi'
*/
export const DT_OSI_PREFIX = "dt.host";

/**
* Prefix for 'dt.pgi'
*/
export const DT_PGI_PREFIX = "dt.process";

/**
* Prefix for 'dt.tech'
*/
export const DT_TECH_PREFIX = "dt.tech";

/**
* Prefix for 'dt.telemetry.exporter'
*/
export const DT_TELEMETRY_EXPORTER_PREFIX = "telemetry.exporter";

/**
* Prefix for 'dt.websphere'
*/
export const DT_WEBSPHERE_PREFIX = "dt.websphere";

/**
* Prefix for 'dt.zosconnect_resource'
*/
export const DT_ZOSCONNECT_RESOURCE_PREFIX = "dt.zosconnect";

/**
* Prefix for 'faas_resource'
*/
export const FAAS_RESOURCE_PREFIX = "faas";

/**
* Prefix for 'host'
*/
export const HOST_PREFIX = "host";

/**
* Prefix for 'process'
*/
export const PROCESS_PREFIX = "process";

/**
* Prefix for 'process.executable'
*/
export const PROCESS_EXECUTABLE_PREFIX = "process.executable";

/**
* Prefix for 'process.runtime'
*/
export const PROCESS_RUNTIME_PREFIX = "process.runtime";

/**
* Prefix for 'service'
*/
export const SERVICE_PREFIX = "service";

/**
* Prefix for 'telemetry.sdk'
*/
export const TELEMETRY_SDK_PREFIX = "telemetry.sdk";


// -----------------------------------------------------------------------------

/**
* Name of the cloud provider.
* This attribute expects a value of type string from the enumeration CloudProviderValues.
*/
export const CLOUD_PROVIDER = "cloud.provider";

/**
* The cloud account ID the resource is assigned to.
* This attribute expects a value of type string.
*/
export const CLOUD_ACCOUNT_ID = "cloud.account.id";

/**
* The geographical region the resource is running. Refer to your provider's docs to see the available regions, for example [AWS regions](https://aws.amazon.com/about-aws/global-infrastructure/regions_az/), [Azure regions](https://azure.microsoft.com/en-us/global-infrastructure/geographies/), or [Google Cloud regions](https://cloud.google.com/about/locations).
* This attribute expects a value of type string.
*/
export const CLOUD_REGION = "cloud.region";

/**
* Cloud regions often have multiple, isolated locations known as zones to increase availability. Availability zone represents the zone where the resource is running.
* This attribute expects a value of type string.
*
* Note: Availability zones are called "zones" on Google Cloud.
*/
export const CLOUD_AVAILABILITY_ZONE = "cloud.availability_zone";

/**
* The cloud platform in use.
* This attribute expects a value of type string from the enumeration CloudPlatformValues.
*
* Note: The prefix of the service SHOULD match the one specified in `cloud.provider`.
*/
export const CLOUD_PLATFORM = "cloud.platform";

/**
* Container name.
* This attribute expects a value of type string.
*/
export const CONTAINER_NAME = "container.name";

/**
* Name of the image the container was built on.
* This attribute expects a value of type string.
*/
export const CONTAINER_IMAGE_NAME = "container.image.name";

/**
* Container image tag.
* This attribute expects a value of type string.
*/
export const CONTAINER_IMAGE_TAG = "container.image.tag";

/**
* A project organizes all your Google Cloud resources.
* This attribute expects a value of type string.
*/
export const GCP_PROJECT_ID = "gcp.project.id";

/**
* A region is a specific geographical location where you can host your resources.
* This attribute expects a value of type string.
*/
export const GCP_REGION = "gcp.region";

/**
* A permanent identifier that is unique within your Google Cloud project.
* This attribute expects a value of type string.
*/
export const GCP_INSTANCE_ID = "gcp.instance.id";

/**
* The name to display for the instance in the Cloud Console.
* This attribute expects a value of type string.
*/
export const GCP_INSTANCE_NAME = "gcp.instance.name";

/**
* The name of a resource type.
* This attribute expects a value of type string.
*/
export const GCP_RESOURCE_TYPE = "gcp.resource.type";

/**
* True if the agent is running in a IBM CTG process. Not set otherwise.
* This attribute expects a value of type boolean.
*
* @deprecated Use `dt.tech.agent_detected_main_technology` instead.
*/
export const DT_CTG_DETECTED = "dt.ctg.detected";

/**
* Reports the value of the `DT_CUSTOM_PROP` environment variable as described [here](https://www.dynatrace.com/support/help/how-to-use-dynatrace/process-groups/configuration/define-your-own-process-group-metadata/).
* This attribute expects a value of type string.
*/
export const DT_ENV_VARS_DT_CUSTOM_PROP = "dt.env_vars.dt_custom_prop";

/**
* Reports the value of the `DT_TAGS` environment variable as described [here](https://www.dynatrace.com/support/help/how-to-use-dynatrace/tags-and-metadata/setup/define-tags-based-on-environment-variables/).
* This attribute expects a value of type string.
*/
export const DT_ENV_VARS_DT_TAGS = "dt.env_vars.dt_tags";

/**
* The AWS Lambda initialization type (see [AWS_LAMBDA_INITIALIZATION_TYPE environment variable](https://docs.aws.amazon.com/lambda/latest/dg/configuration-envvars.html#configuration-envvars-runtime)).
* This attribute expects a value of type string from the enumeration DtFaasAwsInitializationTypeValues.
*
* Note: This is especially interesting for runtimes supporting [SnapStart](https://docs.aws.amazon.com/lambda/latest/dg/snapstart.html) (currently only Java).
*/
export const DT_FAAS_AWS_INITIALIZATION_TYPE = "dt.faas.aws.initialization_type";

/**
* True if the agent is running in an IMS SOAP gateway process. Not set otherwise.
* This attribute expects a value of type boolean.
*
* @deprecated Use `dt.tech.agent_detected_main_technology` instead.
*/
export const DT_IMS_DETECTED = "dt.ims.detected";

/**
* The operating system type.
* This attribute expects a value of type string from the enumeration DtOsTypeValues.
*/
export const DT_OS_TYPE = "dt.os.type";

/**
* Human readable (not intended to be parsed) OS version information, like e.g. reported by `ver` or `lsb_release -a` commands.
* This attribute expects a value of type string.
*/
export const DT_OS_DESCRIPTION = "dt.os.description";

/**
* The SNA ID is the Systems Network Architecture (SNA) identifier. It's a unique ID for a given IP address on the network. In combination with the calculated OSI, this is required to calculate the PGI.
* This attribute expects a value of type string.
*/
export const DT_HOST_SNAID = "dt.host.snaid";

/**
* The SMF ID is the name of the LPAR (logical partition), which is a "host" as far as Dynatrace is concerned. In combination with the IP address, this is required to calculate the OSI.
* This attribute expects a value of type string.
*/
export const DT_HOST_SMFID = "dt.host.smfid";

/**
* Similar to net.host.ip for spans. Currently, there is no OpenTelemetry convention for IP addresses on resources.
* This attribute expects a value of type string.
*/
export const DT_HOST_IP = "dt.host.ip";

/**
* The path to the executable.
* This attribute expects a value of type string.
*/
export const DT_PROCESS_EXECUTABLE = "dt.process.executable";

/**
* The command line arguments of the process as a string.
* This attribute expects a value of type string.
*
* Note: Ideally, this is the original "raw" command line including the executable path, but this might not be possible in all frameworks. In such cases, this can be a best-effort recreation of the commandline. Examples are truncated for readability.
*/
export const DT_PROCESS_COMMANDLINE = "dt.process.commandline";

/**
* Process ID (PID) the data belongs to.
* This attribute expects a value of type string.
*/
export const DT_PROCESS_PID = "dt.process.pid";

/**
* The z/OS job name of the process.
* This attribute expects a value of type string.
*/
export const DT_PROCESS_ZOS_JOB_NAME = "dt.process.zos_job_name";

/**
* The main technology the agent has detected.
* This attribute expects a value of type string from the enumeration DtTechAgentDetectedMainTechnologyValues.
*/
export const DT_TECH_AGENT_DETECTED_MAIN_TECHNOLOGY = "dt.tech.agent_detected_main_technology";

/**
* The exporter name. MUST be `odin` for ODIN protocol.
* This attribute expects a value of type string from the enumeration TelemetryExporterNameValues.
*/
export const TELEMETRY_EXPORTER_NAME = "telemetry.exporter.name";

/**
* The full agent/exporter version.
* This attribute expects a value of type string.
*/
export const TELEMETRY_EXPORTER_VERSION = "telemetry.exporter.version";

/**
* The version as exposed to the package manager-.
* This attribute expects a value of type string.
*
* Note: Many package managers won't accept the sprint + timestamp version format as required for `version` if `name` is `odin` but instead need a semver-compatible string with the intended meaning (for example, the `-` used in the sprint version indicates a pre-release version). That package version MAY be provided in this informational attribute.
*/
export const TELEMETRY_EXPORTER_PACKAGE_VERSION = "telemetry.exporter.package_version";

/**
* Name of the WebSphere server.
* This attribute expects a value of type string.
*/
export const DT_WEBSPHERE_SERVER_NAME = "dt.websphere.server_name";

/**
* Name of the WebSphere node the application is running on.
* This attribute expects a value of type string.
*/
export const DT_WEBSPHERE_NODE_NAME = "dt.websphere.node_name";

/**
* Name of the WebSphere cell the application is running in.
* This attribute expects a value of type string.
*/
export const DT_WEBSPHERE_CELL_NAME = "dt.websphere.cell_name";

/**
* Name of the WebSphere cluster the application is running in.
* This attribute expects a value of type string.
*/
export const DT_WEBSPHERE_CLUSTER_NAME = "dt.websphere.cluster_name";

/**
* Set to true if the agent is running in a z/OS Connect EE server process.
* This attribute expects a value of type boolean.
*
* Note: This value is normally either unset or `true`.
*
* @deprecated Use `dt.tech.agent_detected_main_technology` instead.
*/
export const DT_ZOSCONNECT_DETECTED = "dt.zosconnect.detected";

/**
* The name of the single function that this runtime instance executes.
* This attribute expects a value of type string.
*
* Note: This is the name of the function as configured/deployed on the FaaS platform and is usually different from the name of the callback function (which may be stored in the [`code.namespace`/`code.function`](../../trace/semantic_conventions/span-general.md#source-code-attributes) span attributes).
*/
export const FAAS_NAME = "faas.name";

/**
* The unique ID of the single function that this runtime instance executes.
* This attribute expects a value of type string.
*
* Note: Depending on the cloud provider, use:

* **AWS Lambda:** The function [ARN](https://docs.aws.amazon.com/general/latest/gr/aws-arns-and-namespaces.html).
Take care not to use the "invoked ARN" directly but replace any
[alias suffix](https://docs.aws.amazon.com/lambda/latest/dg/configuration-aliases.html) with the resolved function version, as the same runtime instance may be invokable with multiple
different aliases.
* **GCP:** The [URI of the resource](https://cloud.google.com/iam/docs/full-resource-names)
* **Azure:** The [Fully Qualified Resource ID](https://docs.microsoft.com/en-us/rest/api/resources/resources/get-by-id).

On some providers, it may not be possible to determine the full ID at startup,
which is why this field cannot be made required. For example, on AWS the account ID
part of the ARN is not available without calling another AWS API
which may be deemed too slow for a short-running lambda function.
As an alternative, consider setting `faas.id` as a span attribute instead.
*/
export const FAAS_ID = "faas.id";

/**
* The immutable version of the function being executed.
* This attribute expects a value of type string.
*
* Note: Depending on the cloud provider and platform, use:

* **AWS Lambda:** The [function version](https://docs.aws.amazon.com/lambda/latest/dg/configuration-versions.html)
  (an integer represented as a decimal string).
* **Google Cloud Run:** The [revision](https://cloud.google.com/run/docs/managing/revisions)
  (i.e., the function name plus the revision suffix).
* **Google Cloud Functions:** The value of the
  [`K_REVISION` environment variable](https://cloud.google.com/functions/docs/env-var#runtime_environment_variables_set_automatically).
* **Azure Functions:** Not applicable. Do not set this attribute.
*/
export const FAAS_VERSION = "faas.version";

/**
* The execution environment ID as a string, that will be potentially reused for other invocations to the same function/function version.
* This attribute expects a value of type string.
*
* Note: * **AWS Lambda:** Use the (full) log stream name.
*/
export const FAAS_INSTANCE = "faas.instance";

/**
* The amount of memory available to the serverless function in MiB.
* This attribute expects a value of type int.
*
* Note: It's recommended to set this attribute since e.g. too little memory can easily stop a Java AWS Lambda function from working correctly. On AWS Lambda, the environment variable `AWS_LAMBDA_FUNCTION_MEMORY_SIZE` provides this information.
*/
export const FAAS_MAX_MEMORY = "faas.max_memory";

/**
* Hostname of the host. It contains what the `hostname` command returns on the host machine.
* This attribute expects a value of type string.
*
* @deprecated Deprecated. Use attribute `host.name` instead.
*/
export const HOST_HOSTNAME = "host.hostname";

/**
* Unique host id.
* This attribute expects a value of type string.
*
* Note: For Cloud this must be the instance_id assigned by the cloud provider.
*/
export const HOST_ID = "host.id";

/**
* Name of the host. It may contain what `hostname` returns on Unix systems, the fully qualified, or a name specified by the user.
* This attribute expects a value of type string.
*/
export const HOST_NAME = "host.name";

/**
* Type of host.
* This attribute expects a value of type string.
*
* Note: For Cloud this must be the machine type.
*/
export const HOST_TYPE = "host.type";

/**
* Name of the VM image or OS install the host was instantiated from.
* This attribute expects a value of type string.
*/
export const HOST_IMAGE_NAME = "host.image.name";

/**
* VM image id.
* This attribute expects a value of type string.
*
* Note: For Cloud, this value is from the provider.
*/
export const HOST_IMAGE_ID = "host.image.id";

/**
* The version string of the VM image as defined in [Version Attributes](https://github.com/open-telemetry/opentelemetry-specification/blob/v1.23.0/specification/resource/semantic_conventions/README.md#version-attributes).
* This attribute expects a value of type string.
*/
export const HOST_IMAGE_VERSION = "host.image.version";

/**
* The CPU architecture the host system is running on.
* This attribute expects a value of type string from the enumeration HostArchValues.
*/
export const HOST_ARCH = "host.arch";

/**
* Process identifier (PID).
* This attribute expects a value of type int.
*/
export const PROCESS_PID = "process.pid";

/**
* The username of the user that owns the process.
* This attribute expects a value of type string.
*/
export const PROCESS_OWNER = "process.owner";

/**
* The command used to launch the process (i.e. the command name). On Linux based systems, can be set to the zeroth string in `proc/[pid]/cmdline`. On Windows, can be set to the first parameter extracted from `GetCommandLineW`.
* This attribute expects a value of type string.
*/
export const PROCESS_COMMAND = "process.command";

/**
* The full command used to launch the process. The value can be either a list of strings representing the ordered list of arguments, or a single string representing the full command. On Linux based systems, can be set to the list of null-delimited strings extracted from `proc/[pid]/cmdline`. On Windows, can be set to the result of `GetCommandLineW`.
* This attribute expects a value of type string[].
*
* Note: Union types are not yet supported, so instead of `string` a single-element array is used.
*/
export const PROCESS_COMMAND_LINE = "process.command_line";

/**
* The name of the process executable. On Linux based systems, can be set to the `Name` in `proc/[pid]/status`. On Windows, can be set to the base name of `GetProcessImageFileNameW`.
* This attribute expects a value of type string.
*/
export const PROCESS_EXECUTABLE_NAME = "process.executable.name";

/**
* The full path to the process executable. On Linux based systems, can be set to the target of `proc/[pid]/exe`. On Windows, can be set to the result of `GetProcessImageFileNameW`.
* This attribute expects a value of type string.
*/
export const PROCESS_EXECUTABLE_PATH = "process.executable.path";

/**
* The name of the runtime of this process.
* This attribute expects a value of type string from the enumeration ProcessRuntimeNameValues.
*
* Note: SHOULD be set to one of the values listed below, unless more detailed instructions are provided. If none of the listed values apply, a custom value best describing the runtime CAN be used. For compiled native binaries, this SHOULD be the name of the compiler.
*/
export const PROCESS_RUNTIME_NAME = "process.runtime.name";

/**
* The version of the runtime of this process, as returned by the runtime without modification.
* This attribute expects a value of type string.
*/
export const PROCESS_RUNTIME_VERSION = "process.runtime.version";

/**
* An additional description about the runtime of the process, for example a specific vendor customization of the runtime environment.
* This attribute expects a value of type string.
*/
export const PROCESS_RUNTIME_DESCRIPTION = "process.runtime.description";

/**
* Logical name of the service.
* This attribute expects a value of type string.
*
* Note: MUST be the same for all instances of horizontally scaled services.
*/
export const SERVICE_NAME = "service.name";

/**
* A namespace for `service.name`.
* This attribute expects a value of type string.
*
* Note: A string value having a meaning that helps to distinguish a group of services, for example the team name that owns a group of services. `service.name` is expected to be unique within the same namespace. The field is optional. If `service.namespace` is not specified in the Resource then `service.name` is expected to be unique for all services that have no explicit namespace defined (so the empty/unspecified namespace is simply one more valid namespace). Zero-length namespace string is assumed equal to unspecified namespace.
*/
export const SERVICE_NAMESPACE = "service.namespace";

/**
* The string ID of the service instance.
* This attribute expects a value of type string.
*
* Note: MUST be unique for each instance of the same `service.namespace,service.name` pair (in other words `service.namespace,service.name,service.id` triplet MUST be globally unique). The ID helps to distinguish instances of the same service that exist at the same time (e.g. instances of a horizontally scaled service). It is preferable for the ID to be persistent and stay the same for the lifetime of the service instance, however it is acceptable that the ID is ephemeral and changes during important lifetime events for the service (e.g. service restarts). If the service has no inherent unique ID that can be used as the value of this attribute it is recommended to generate a random Version 1 or Version 4 RFC 4122 UUID (services aiming for reproducible UUIDs may also use Version 5, see RFC 4122 for more recommendations).
*/
export const SERVICE_INSTANCE_ID = "service.instance.id";

/**
* The version string of the service API or implementation as defined in [Version Attributes](https://github.com/open-telemetry/opentelemetry-specification/blob/v1.23.0/specification/resource/semantic_conventions/README.md#version-attributes).
* This attribute expects a value of type string.
*/
export const SERVICE_VERSION = "service.version";

/**
* The name of the telemetry SDK as defined above.
* This attribute expects a value of type string.
*
* Note: The default OpenTelemetry SDK provided by the OpenTelemetry project MUST set `telemetry.sdk.name` to the value opentelemetry. If another SDK, like a fork or a vendor-provided implementation, is used, this SDK MUST set the attribute `telemetry.sdk.name` to the fully-qualified class or module name of this SDK's main entry point or another suitable identifier depending on the language. The identifier `opentelemetry` is reserved and MUST NOT be used in this case. The identifier SHOULD be stable across different versions of an implementation.
*/
export const TELEMETRY_SDK_NAME = "telemetry.sdk.name";

/**
* The language of the telemetry SDK.
* This attribute expects a value of type string from the enumeration TelemetrySdkLanguageValues.
*/
export const TELEMETRY_SDK_LANGUAGE = "telemetry.sdk.language";

/**
* The version string of the service API or implementation as defined in [Version Attributes](https://github.com/open-telemetry/opentelemetry-specification/blob/v1.23.0/specification/resource/semantic_conventions/README.md#version-attributes).
* This attribute expects a value of type string.
*/
export const TELEMETRY_SDK_VERSION = "telemetry.sdk.version";


// Enum definitions
export enum CloudProviderValues {
   AWS = "aws",
   AZURE = "azure",
   GCP = "gcp",
}

export enum CloudPlatformValues {
   AWS_EC2 = "aws_ec2",
   AWS_ECS = "aws_ecs",
   AWS_EKS = "aws_eks",
   AWS_LAMBDA = "aws_lambda",
   AWS_ELASTIC_BEANSTALK = "aws_elastic_beanstalk",
   AZURE_VM = "azure_vm",
   AZURE_CONTAINER_INSTANCES = "azure_container_instances",
   AZURE_AKS = "azure_aks",
   AZURE_FUNCTIONS = "azure_functions",
   AZURE_APP_SERVICE = "azure_app_service",
   GCP_COMPUTE_ENGINE = "gcp_compute_engine",
   GCP_CLOUD_RUN = "gcp_cloud_run",
   GCP_KUBERNETES_ENGINE = "gcp_kubernetes_engine",
   GCP_CLOUD_FUNCTIONS = "gcp_cloud_functions",
   GCP_APP_ENGINE = "gcp_app_engine",
}

export enum DtFaasAwsInitializationTypeValues {
   ON_DEMAND = "on-demand",
   PROVISIONED_CONCURRENCY = "provisioned-concurrency",
   SNAP_START = "snap-start",
}

export enum DtOsTypeValues {
   UNKNOWN = "UNKNOWN",
   WINDOWS = "WINDOWS",
   LINUX = "LINUX",
   HPUX = "HPUX",
   AIX = "AIX",
   SOLARIS = "SOLARIS",
   ZOS = "ZOS",
   DARWIN = "DARWIN",
}

export enum DtTechAgentDetectedMainTechnologyValues {
   UNKNOWN = "unknown",
   AWS_LAMBDA = "aws_lambda",
   ZOS_CONNECT = "zos_connect",
   CTG = "ctg",
   IMS = "ims",
   WEBSPHERE_AS = "websphere_as",
   WEBSPHERE_LIBERTY = "websphere_liberty",
}

export enum TelemetryExporterNameValues {
   ODIN = "odin",
}

export enum HostArchValues {
   AMD64 = "amd64",
   ARM32 = "arm32",
   ARM64 = "arm64",
   IA64 = "ia64",
   PPC32 = "ppc32",
   PPC64 = "ppc64",
   S390X = "s390x",
   X86 = "x86",
}

export enum ProcessRuntimeNameValues {
   BEAM = "BEAM",
   GC = "Go compiler",
   GCCGO = "GCC Go frontend",
   NODEJS = "NodeJS",
   BROWSER = "Web Browser",
   IOJS = "io.js",
   DOTNET_CORE = ".NET Core, .NET 5+",
   DOTNET_FRAMEWORK = ".NET Framework",
   MONO = "Mono",
   CPYTHON = "CPython",
   IRONPYTHON = "IronPython",
   JYTHON = "Jython",
   PYPY = "PyPy",
   PYTHONNET = "PythonNet",
}

export enum TelemetrySdkLanguageValues {
   CPP = "cpp",
   DOTNET = "dotnet",
   ERLANG = "erlang",
   GO = "go",
   JAVA = "java",
   NODEJS = "nodejs",
   PHP = "php",
   PYTHON = "python",
   RUBY = "ruby",
   WEBJS = "webjs",
}
